import { EventEmitter } from 'events';
import { LocalNotifications, ScheduleOptions, LocalNotificationSchema } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { theme } from '../lib/theme';

interface NotificationData {
  title: string;
  body: string;
  id?: number;
  schedule?: { at: Date };
  deepLink?: string;
  type?: string;
  metadata?: Record<string, any>;
  icon?: string;
}

class UnifiedNotificationService extends EventEmitter {
  private static instance: UnifiedNotificationService;
  private hasNativePermission: boolean = false;
  private hasWebPermission: boolean = false;
  private readonly notificationAccent: string = theme.notificationAccent;

  private constructor() {
    super();
    console.log('[Notifications] Initializing UnifiedNotificationService');
    this.initialize();
  }

  public static getInstance(): UnifiedNotificationService {
    if (!UnifiedNotificationService.instance) {
      UnifiedNotificationService.instance = new UnifiedNotificationService();
    }
    return UnifiedNotificationService.instance;
  }

  private async initialize() {
    console.log('[Notifications] Initializing UnifiedNotificationService');
    console.log('[Notifications] Is native platform:', Capacitor.isNativePlatform());
    console.log('[Notifications] Platform:', Capacitor.getPlatform());

    try {
      // Check and request permissions for both platforms
      await this.checkAndRequestPermissions();
      
      // Setup native notification handlers if on mobile
      if (Capacitor.isNativePlatform()) {
        await this.setupNativeNotifications();
      }
    } catch (error) {
      console.error('[Notifications] Error during initialization:', error);
    }
  }

  private async setupNativeNotifications() {
    try {
      console.log('[Notifications] Setting up native notifications');
      
      // Register notification channels for Android
      if (Capacitor.getPlatform() === 'android') {
        // Delete existing channels first to ensure settings are updated
        try {
          await LocalNotifications.deleteChannel({ id: 'bookings' });
          await LocalNotifications.deleteChannel({ id: 'chat' });
          console.log('[Notifications] Deleted existing channels');
        } catch (e) {
          // Channels might not exist, which is fine
        }

        // Create booking notification channel
        await LocalNotifications.createChannel({
          id: 'bookings',
          name: 'Booking Notifications',
          description: 'Notifications for bookings and appointments',
          importance: 5, // High importance
          visibility: 1, // Public
          vibration: true,
          lights: true,
          sound: 'default',
          lightColor: this.notificationAccent
        });

        // Create chat notification channel
        await LocalNotifications.createChannel({
          id: 'chat',
          name: 'Chat Notifications',
          description: 'Notifications for chat messages',
          importance: 5, // High importance
          visibility: 1, // Public
          vibration: true,
          lights: true,
          sound: 'default',
          lightColor: this.notificationAccent
        });

        console.log('[Notifications] Created Android notification channels');

        // Test if channels were created
        const channels = await LocalNotifications.listChannels();
        console.log('[Notifications] Available channels:', channels);
      }

      // Setup notification handlers
      await this.setupNativeHandlers();

      console.log('[Notifications] Native notifications setup complete');
    } catch (error) {
      console.error('[Notifications] Error setting up native notifications:', error);
    }
  }

  public async checkAndRequestPermissions(): Promise<void> {
    try {
      // Check web notifications permission
      if ('Notification' in window) {
        if (Notification.permission === 'granted') {
          this.hasWebPermission = true;
        } else if (Notification.permission !== 'denied') {
          const permission = await Notification.requestPermission();
          this.hasWebPermission = permission === 'granted';
        }
        console.log('[Notifications] Web notification permission:', this.hasWebPermission);
      }

      // Check native notifications permission
      if (Capacitor.isNativePlatform()) {
        console.log('[Notifications] Checking native notification permissions');
        const permStatus = await LocalNotifications.checkPermissions();
        console.log('[Notifications] Current permission status:', permStatus);

        if (permStatus.display === 'granted') {
          this.hasNativePermission = true;
        } else if (permStatus.display === 'prompt') {
          console.log('[Notifications] Requesting native notification permissions');
          const requested = await LocalNotifications.requestPermissions();
          console.log('[Notifications] Permission request result:', requested);
          this.hasNativePermission = requested.display === 'granted';
        }
        console.log('[Notifications] Native notification permission:', this.hasNativePermission);
      }
    } catch (error) {
      console.error('[Notifications] Error checking permissions:', error);
    }
  }

  private async setupNativeHandlers() {
    try {
      // Remove any existing listeners first
      await LocalNotifications.removeAllListeners();

      LocalNotifications.addListener('localNotificationReceived', (notification) => {
        console.log('[Notifications] Notification received:', notification);
        this.emit('notificationReceived', notification);
      });

      LocalNotifications.addListener('localNotificationActionPerformed', (notification) => {
        console.log('[Notifications] Notification action performed:', notification);
        const data = notification.notification.extra;
        if (data?.deepLink) {
          window.dispatchEvent(new CustomEvent('app-navigate', { detail: { path: data.deepLink } }));
        }
        this.emit('notificationClicked', notification);
      });

      console.log('[Notifications] Native handlers setup complete');
    } catch (error) {
      console.error('[Notifications] Error setting up notification handlers:', error);
    }
  }

  private getChannelId(type?: string): string {
    switch (type) {
      case 'chat':
      case 'new_message':
        return 'chat';
      case 'booking':
      case 'booking_confirmation':
      case 'booking_reminder_24h':
      case 'booking_reminder_1h':
      default:
        return 'bookings';
    }
  }

  public async showNotification(data: NotificationData) {
    // For web platform
    if (!Capacitor.isNativePlatform()) {
      console.log('[Notifications] Processing web notification:', data);

      // Check if web notifications are supported
      if (!('Notification' in window)) {
        console.error('[Notifications] Web notifications not supported');
        return false;
      }

      // Check and request permission if needed
      if (Notification.permission !== 'granted') {
        console.log('[Notifications] Requesting notification permission');
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          console.error('[Notifications] Notification permission not granted');
          return false;
        }
      }

      // If this is a scheduled notification, don't show it immediately
      if (data.schedule?.at) {
        const scheduledTime = new Date(data.schedule.at);
        const now = new Date();
        
        // If the scheduled time is in the future, just return true
        if (scheduledTime > now) {
          console.log('[Notifications] Notification scheduled for:', scheduledTime);
          return true;
        }
      }

      // For immediate notifications or past scheduled times
      try {
        console.log('[Notifications] Creating web notification:', {
          title: data.title,
          body: data.body,
          icon: data.icon || '/icon.png',
          tag: data.type || 'default'
        });

        const notification = new Notification(data.title, {
          body: data.body,
          icon: data.icon || '/icon.png',
          tag: data.type || 'default',
          data: {
            ...data.metadata,
            deepLink: data.deepLink
          }
        });

        notification.onclick = () => {
          console.log('[Notifications] Notification clicked, navigating to:', data.deepLink);
          if (data.deepLink) {
            window.dispatchEvent(new CustomEvent('app-navigate', { detail: { path: data.deepLink } }));
          }
          notification.close();
        };

        console.log('[Notifications] Web notification created successfully');
        return true;
      } catch (error) {
        console.error('[Notifications] Error showing web notification:', error);
        return false;
      }
    }

    // For native platform (rest of the existing code)
    try {
      // Recheck permissions
      const permStatus = await LocalNotifications.checkPermissions();
      if (permStatus.display !== 'granted') {
        console.log('[Notifications] Requesting permissions again');
        const requested = await LocalNotifications.requestPermissions();
        if (requested.display !== 'granted') {
          console.error('[Notifications] Permission not granted');
          return false;
        }
      }

      // Verify channel exists
      if (Capacitor.getPlatform() === 'android') {
        const channels = await LocalNotifications.listChannels();
        const hasChannel = channels.channels.some(c => c.id === 'bookings');
        if (!hasChannel) {
          console.log('[Notifications] Recreating notification channel');
          await LocalNotifications.createChannel({
            id: 'bookings',
            name: 'Booking Notifications',
            description: 'Notifications for bookings and appointments',
            importance: 5,
            visibility: 1,
            vibration: true,
            lights: true,
            sound: 'default',
            lightColor: this.notificationAccent
          });
        }
      }

      // Generate a unique ID that won't conflict
      const notificationId = Math.floor(Math.random() * 100000) + 1;

      // Create notification with minimal required properties first
      const baseNotification: LocalNotificationSchema = {
        id: notificationId,
        title: data.title,
        body: data.body,
        channelId: this.getChannelId(data.type),
        smallIcon: 'ic_notification',
        iconColor: this.notificationAccent,
        sound: 'default',
        autoCancel: true
      };

      // Add schedule if provided
      if (data.schedule?.at) {
        baseNotification.schedule = {
          at: new Date(data.schedule.at)
        };
      }

      // Add extra data if provided
      if (data.metadata || data.deepLink) {
        baseNotification.extra = {
          ...data.metadata,
          deepLink: data.deepLink
        };
      }

      await LocalNotifications.schedule({
        notifications: [baseNotification]
      });

      return true;
    } catch (error) {
      console.error('[Notifications] Error scheduling native notification:', error);
      return false;
    }
  }

  public async showBookingNotification(
    bookingId: string,
    serviceName: string,
    dateTime: Date,
    queueNumber: string
  ) {
    console.log('[Notifications] Creating booking notification:', {
      bookingId,
      serviceName,
      dateTime,
      queueNumber
    });

    // Create a simple notification data object
    const data: NotificationData = {
      title: 'Booking Confirmed ✓',
      body: `Your appointment for ${serviceName} has been successfully booked for ${dateTime.toLocaleString()}.\nQueue number: ${queueNumber}`,
      type: 'booking_confirmation',
      metadata: {
        bookingId,
        serviceName,
        dateTime: dateTime.toISOString(),
        queueNumber
      }
    };

    // Add deepLink only if we're not on native platform (to avoid potential issues)
    if (!Capacitor.isNativePlatform()) {
      data.deepLink = `/bookings/${bookingId}`;
    }

    // For web platform, show notification directly
    if (!Capacitor.isNativePlatform() && 'Notification' in window) {
      try {
        const notification = new Notification(data.title, {
          body: data.body,
          icon: data.icon || '/icon.png',
          tag: data.type || 'default',
          data: {
            ...data.metadata,
            deepLink: data.deepLink
          }
        });

        notification.onclick = () => {
          if (data.deepLink) {
            window.location.href = data.deepLink;
          }
          notification.close();
        };

        console.log('[Notifications] Booking notification shown');
        return true;
      } catch (error) {
        console.error('[Notifications] Error showing booking notification:', error);
        return false;
      }
    }

    // For native platform, use the native notification system
    return this.showNotification(data);
  }

  public async scheduleReminder(
    bookingId: string,
    serviceName: string,
    appointmentTime: Date,
    queueNumber: string,
    reminderTime: Date
  ) {
    console.log('[Notifications] Scheduling reminder:', {
      bookingId,
      serviceName,
      appointmentTime,
      queueNumber,
      reminderTime
    });

    // Calculate time difference to determine reminder type
    const timeDiff = appointmentTime.getTime() - reminderTime.getTime();
    const hoursDiff = timeDiff / (1000 * 60 * 60);

    // Create a simple notification data object
    const data: NotificationData = {
      title: hoursDiff >= 24 ? '24h Appointment Reminder' : '1h Appointment Reminder',
      body: hoursDiff >= 24 
        ? `Reminder: Your appointment for ${serviceName} is tomorrow at ${appointmentTime.toLocaleTimeString()}.\nQueue number: ${queueNumber}`
        : `Reminder: Your appointment for ${serviceName} is in 1 hour.\nQueue number: ${queueNumber}`,
      type: hoursDiff >= 24 ? 'booking_reminder_24h' : 'booking_reminder_1h',
      schedule: { at: reminderTime },
      metadata: {
        bookingId,
        serviceName,
        appointmentTime: appointmentTime.toISOString(),
        queueNumber
      }
    };

    // Add deepLink only if we're not on native platform
    if (!Capacitor.isNativePlatform()) {
      data.deepLink = `/bookings/${bookingId}`;
    }

    return this.showNotification(data);
  }

  public async cleanupNotifications() {
    if (!Capacitor.isNativePlatform()) return;

    try {
      console.log('[Notifications] Cleaning up notifications');
      
      // Get all pending notifications
      const pending = await LocalNotifications.getPending();
      console.log('[Notifications] Pending notifications before cleanup:', pending);

      // Cancel all pending notifications
      if (pending.notifications.length > 0) {
        const pendingIds = pending.notifications.map(n => n.id);
        await LocalNotifications.cancel({ notifications: pending.notifications });
        console.log('[Notifications] Cancelled pending notifications:', pendingIds);
      }

      // Clear delivered notifications
      await LocalNotifications.removeAllDeliveredNotifications();
      console.log('[Notifications] Cleared all delivered notifications');
    } catch (error) {
      console.error('[Notifications] Error cleaning up notifications:', error);
    }
  }

  public async cancelNotification(id: number) {
    if (!Capacitor.isNativePlatform()) return;

    try {
      console.log('[Notifications] Cancelling notification:', id);
      await LocalNotifications.cancel({ notifications: [{ id }] });
    } catch (error) {
      console.error('[Notifications] Error cancelling notification:', error);
    }
  }

  public cleanupListeners(): void {
    if (Capacitor.isNativePlatform()) {
      LocalNotifications.removeAllListeners();
    }
    this.removeAllListeners();
  }
}

export const unifiedNotificationService = UnifiedNotificationService.getInstance(); 